import dynamic from 'next/dynamic';
import { memo, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { HelpDropdownButton } from '~/components/HelpDropdownButton';
import { Banners } from '~/components/Layouts/HomeLayout/Banners';
import { BoardImportDropTarget } from '~/components/Shared/Drag/BoardImportDropTarget/BoardImportDropTarget';
import { StatusBar } from '~/components/v2/components/shared/status-bar/StatusBar';
import { LibraryActions } from '~/components/v2/components/workspace/workspace-header/LibraryActions';
import { WorkspaceHeader } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeader';
import { WorkspaceNotification } from '~/components/v2/components/workspace/workspace-notification/WorkspaceNotification';
import { WorkspaceSidebarToken } from '~/components/v2/components/workspace/workspace-sidebar/getWorkspaceSidebarEventToken';
import { WorkspaceSidebar } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebar';
import { WorkspaceSidebarResizer } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarResizer';
import { useIsCanvasOverlayMode, useIsCanvasVisible } from '~/components/v2/hooks/useIsCanvasVisible';
import { useTailwindFlag } from '~/components/v2/hooks/useTailwindFlag';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { useSelectedCanvasIdSync } from '~/hooks/canvases/useSelectedCanvasIdSync';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getWorkspaceContentStyle } from '~/utils/getWorkspaceContentStyle';

const WINDOW_RESIZE_DEBOUNCE_MS = 150;
const WorkspaceRightPanel = dynamic(
  () =>
    import('~/components/v2/components/workspace/workspace-right-panel/WorkspaceRightPanel').then(
      (mod) => mod.WorkspaceRightPanel,
    ),
  { ssr: false },
);

export type PrivateDefaultLayoutProps = {
  bottomBar?: ReactNode;
  children: ReactNode;
  getLibraryActions?: boolean;
  headerActions?: ReactNode;
  search?: ReactNode;
};

export const PrivateDefaultLayout = memo(
  ({ bottomBar, children, getLibraryActions = false, headerActions, search }: PrivateDefaultLayoutProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
    const isCanvasVisible = useIsCanvasVisible();
    const isCanvasOverlayMode = useIsCanvasOverlayMode();
    useSelectedCanvasIdSync({ enabled: generativeCanvasEnabled });

    const boardId = useURLBoardIdSelector();
    const rightPanelOpen = useWorkspaceRightPanelOpen();
    const isLeftNavResizing = useWorkspaceSidebarStore((state) => state.isResizing);
    const setNotificationsOpen = useWorkspaceNotificationsStore((state) => state.setOpen);
    const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
    const { isAboveXLScreen } = useBreakpointsContext();
    useTailwindFlag();
    const [isRightPanelResizing, setIsRightPanelResizing] = useState(false);
    const handleRightPanelResizeStart = useCallback(() => setIsRightPanelResizing(true), []);
    const handleRightPanelResizeEnd = useCallback(() => setIsRightPanelResizing(false), []);
    const [isWindowResizing, setIsWindowResizing] = useState(false);
    const windowResizeTimerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
      const handleResize = () => {
        setIsWindowResizing(true);
        clearTimeout(windowResizeTimerRef.current);
        windowResizeTimerRef.current = setTimeout(() => setIsWindowResizing(false), WINDOW_RESIZE_DEBOUNCE_MS);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(windowResizeTimerRef.current);
      };
    }, []);
    const leftSidebarOpen = useWorkspaceSidebarStore((state) => state.open);
    const sidebarPushOpen = useWorkspaceSidebarPushStore((s) => s.isOpen);
    const closePush = useWorkspaceSidebarPushStore((s) => s.closePush);
    const deleteLockToken = useWorkspaceSidebarPushStore((s) => s.deleteLockToken);
    const resetPush = useWorkspaceSidebarPushStore((s) => s.reset);
    const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);
    const setLeftSidebarOpen = useWorkspaceSidebarStore((state) => state.setOpen);
    const setPortal = useWorkspaceSelectionBarStore((state) => state.setPortal);

    useEffect(() => {
      if (leftSidebarOpen) {
        resetPush();
      }
    }, [leftSidebarOpen, resetPush]);

    const leftSidebarOpenRef = useRef(leftSidebarOpen);
    leftSidebarOpenRef.current = leftSidebarOpen;

    const wasFullscreenRef = useRef(isFullscreen);
    const sidebarWasOpenBeforeFullscreenRef = useRef(false);
    useEffect(() => {
      const wasFullscreen = wasFullscreenRef.current;
      wasFullscreenRef.current = isFullscreen;

      if (isFullscreen && !wasFullscreen) {
        sidebarWasOpenBeforeFullscreenRef.current = leftSidebarOpenRef.current;
        setLeftSidebarOpen(false);
      } else if (!isFullscreen && wasFullscreen) {
        // If the canvas is also closing, always pin the nav.
        // If the user is just exiting fullscreen while keeping the canvas open, restore pre-fullscreen state.
        setLeftSidebarOpen(rightPanelOpen ? sidebarWasOpenBeforeFullscreenRef.current : true);
      }
    }, [isFullscreen, rightPanelOpen, setLeftSidebarOpen]);

    // Auto-collapse left nav when right panel opens (unless in overlay mode).
    // Only restore sidebar on canvas close if it was open when canvas opened —
    // don't force it open if the user had already closed it manually.
    const prevRightPanelOpenRef = useRef(rightPanelOpen);
    const sidebarWasOpenRef = useRef(false);
    useEffect(() => {
      const wasOpen = prevRightPanelOpenRef.current;
      prevRightPanelOpenRef.current = rightPanelOpen;

      if (rightPanelOpen && !wasOpen && !isAboveXLScreen) {
        sidebarWasOpenRef.current = leftSidebarOpenRef.current;
        setLeftSidebarOpen(false);
      }
      if (!rightPanelOpen && wasOpen) {
        if (sidebarWasOpenRef.current) {
          setLeftSidebarOpen(true);
        }
        sidebarWasOpenRef.current = false;
      }
    }, [rightPanelOpen, isAboveXLScreen, setLeftSidebarOpen]);

    useEffect(() => {
      if (isWorkspaceNavPushMode && leftSidebarOpen) {
        sidebarWasOpenRef.current = true;
        setLeftSidebarOpen(false);
      }
    }, [isWorkspaceNavPushMode, leftSidebarOpen, setLeftSidebarOpen]);

    useLayoutEffect(() => {
      const isPinned = leftSidebarOpen && !isWorkspaceNavPushMode && !isFullscreen;
      document.documentElement.style.setProperty('--left-nav-width', isPinned ? 'var(--sidebar-width)' : '0px');
    }, [leftSidebarOpen, isWorkspaceNavPushMode, isFullscreen]);

    const showLayoutHelpButton = !rightPanelOpen && !bottomBar;

    const contentStyle = getWorkspaceContentStyle({
      rightPanelOpen,
      isFullscreen,
    });

    const handleAppHover = useCallback(() => {
      const isNotificationsOpen = useWorkspaceNotificationsStore.getState().open;

      if (isWorkspaceNavPushMode && isNotificationsOpen) {
        return;
      }

      if (isNotificationsOpen) {
        setNotificationsOpen(false);
      }

      if (isWorkspaceNavPushMode || !leftSidebarOpen) {
        deleteLockToken(WorkspaceSidebarToken.Notifications);
        closePush();
      }
    }, [isWorkspaceNavPushMode, leftSidebarOpen, setNotificationsOpen, deleteLockToken, closePush]);

    return (
      <div
        className="group/layout flex size-full flex-col bg-grey-4"
        data-sidebar-overlay={sidebarPushOpen && !leftSidebarOpen ? 'open' : undefined}
      >
        {currentWorkspace && <Banners />}
        {!rightPanelOpen && (
          <div onPointerEnter={handleAppHover}>
            <WorkspaceHeader
              actions={
                <>
                  {headerActions}
                  {getLibraryActions && <LibraryActions />}
                </>
              }
              search={search}
            />
          </div>
        )}
        <div className="relative flex min-h-0 grow overflow-x-hidden">
          <WorkspaceSidebar />
          <WorkspaceNotification />

          <div
            className={cn(
              'relative flex min-h-0 grow overflow-hidden group-data-[sidebar-overlay=open]/layout:ml-[var(--sidebar-width)]',
              rightPanelOpen && 'pt-2',
              isRightPanelResizing || isLeftNavResizing || isWindowResizing || leftSidebarOpen
                ? 'transition-none'
                : 'transition-[margin-left] duration-200 ease-out',
              isCanvasVisible && rightPanelOpen && (isWorkspaceNavPushMode || sidebarPushOpen) && 'w-full shrink-0',
            )}
          >
            <div
              className="relative flex w-full flex-col overflow-hidden"
              data-workspace-content
              style={isCanvasVisible ? contentStyle : undefined}
            >
              <div className="flex min-h-0 grow overflow-hidden">
                <div
                  className={cn(
                    'relative flex grow gap-2 px-2',
                    isCanvasVisible || !!bottomBar || showLayoutHelpButton ? 'pb-10' : 'pb-2',
                  )}
                  onPointerEnter={handleAppHover}
                  ref={setPortal}
                >
                  <WorkspaceSidebarResizer />
                  {isCanvasVisible && <BoardImportDropTarget boardId={boardId} />}
                  {children}
                  {bottomBar}
                  {showLayoutHelpButton && (
                    <StatusBar>
                      <span />
                      <HelpDropdownButton />
                    </StatusBar>
                  )}
                </div>
              </div>
            </div>
            {(isCanvasVisible || isCanvasOverlayMode) && (
              <WorkspaceRightPanel
                isOverlay={isCanvasOverlayMode}
                onResizeEnd={handleRightPanelResizeEnd}
                onResizeStart={handleRightPanelResizeStart}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

PrivateDefaultLayout.displayName = 'PrivateDefaultLayout';
